import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoadWithInput } from "@redotech/react-util/load";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";

import { RecognizeMercanaVipBackfill } from "./recognize-mercana-vip-backfill";

interface RecognizeOrdersCardProps {
  teamId: string;
  enabled: boolean;
}

export const RecognizeOrdersCard = memo(function RecognizeOrdersCard({
  teamId,
  enabled,
}: RecognizeOrdersCardProps) {
  const client = useRequiredContext(RedoAdminRpcClientContext);
  const [recentCount, setRecentCount] = useState("2000");
  const status = useRpcQuery(RedoAdminRpcClientContext, {
    method: "getRecognizeOrdersStatus",
    args: { teamId },
    options: {
      enabled,
      refetchInterval: (query) => {
        if (query.state.data?.status === "RUNNING") {
          return 5000;
        }
        return false;
      },
    },
  });
  const [run, start] = useTriggerLoadWithInput(async (targetCount: number) => {
    const scope = `enough orders to reach ${targetCount.toLocaleString()} unique successfully enriched profiles`;
    const confirmed = window.confirm(
      `Run Recognize on ${scope} for this store? Eligible research will be reused. This does not run Recognize Actions or OMS automations.`,
    );
    if (!confirmed) {
      return;
    }
    const result = await client.runRecognizeOrders({ teamId, targetCount });
    await status.refetch();
    if (!result.started) {
      toast(result.message, { variant: "error" });
      return;
    }
    toast("Recognize started in the background.", { variant: "success" });
  });
  const count = Number(recentCount);
  const invalidCount =
    !Number.isSafeInteger(count) || count < 1 || count > 10_000;
  const isRunning = status.data?.status === "RUNNING";
  const disabled =
    !enabled || status.isPending || status.isError || run.pending || isRunning;
  let runningLabel = "Running…";
  if (isRunning && status.data) {
    const { completed, failed, skipped, selected } = status.data.progress;
    const processed = (completed + failed + skipped).toLocaleString();
    runningLabel = `Running · ${processed} processed`;
    if (selected !== null) {
      runningLabel = `Running · ${processed} of ${selected.toLocaleString()} processed`;
      if (status.data.progress.mode === "fill") {
        runningLabel = `Running · ${completed.toLocaleString()} of ${selected.toLocaleString()} profiles enriched`;
      }
    }
  }

  return (
    <Card title="Recognize existing orders">
      <Flex dir="column" gap="lg" p="lg">
        <Text fontSize="sm" textColor="secondary">
          Research customers from existing outbound orders using the store’s
          Recognize source filters. Eligible research is reused. This does not
          run Recognize Actions or OMS automations.
        </Text>
        {!enabled && (
          <Text fontSize="sm" textColor="secondary">
            Enable Recognize for this store before running.
          </Text>
        )}
        <Flex align="flex-start" dir="column" gap="md">
          <RedoTextInput
            inputTypeHint="number"
            label="Enriched profile target"
            max={10_000}
            min={1}
            setValue={setRecentCount}
            step={1}
            value={recentCount}
          />
          <Flex align="center" gap="md" wrap="wrap">
            <RedoButton
              disabled={disabled || invalidCount}
              hierarchy="secondary"
              onClick={() => void start(count)}
              size="sm"
              text="Run up to"
            />
            {isRunning && (
              <Text fontSize="sm" textColor="secondary">
                {runningLabel}
              </Text>
            )}
          </Flex>
        </Flex>
        <Divider />
        <Text fontSize="sm" textColor="secondary">
          Run up to counts unique successfully enriched profiles across previous
          runs and continues to older orders after skips or failures. Rerunning
          fills only the remaining gap.
        </Text>
        <Text fontSize="sm" textColor="secondary">
          Mercana runs submit customers in batches of up to 500 and allow up to
          2,000 unfinished customers across stores. Source and country filters
          apply.
        </Text>
        <Divider />
        <RecognizeMercanaVipBackfill enabled={enabled} teamId={teamId} />
        {status.error && (
          <Text fontSize="sm" textColor="error">
            Unable to load run status: {status.error.message}
          </Text>
        )}
        {run.error && (
          <Text fontSize="sm" textColor="error">
            Unable to start research: {run.error.message}
          </Text>
        )}
      </Flex>
    </Card>
  );
});
